import React, { createContext, useContext, useState, useEffect } from "react";
import { validateManifestIcons } from "../../utils/pwaLogoValidator";
import { runPWACheck } from "../../utils/pwaChecker";

const PWAInstallContext = createContext(null);

export function PWAInstallProvider({ children }) {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Comprehensive PWA check on mount
    const performPWACheck = async () => {
      // Wait a bit for service worker to register
      setTimeout(async () => {
        try {
          // Validate manifest icons
          const response = await fetch("/manifest.json");
          const manifest = await response.json();

          if (manifest.icons) {
            // eslint-disable-next-line no-unused-vars
            const validation = validateManifestIcons(manifest.icons);
            // Validation is done silently, results stored in report
          }

          // Run comprehensive PWA check
          const report = await runPWACheck();

          // Store check results for debugging
          if (typeof window !== "undefined") {
            window.__PWA_CHECK_REPORT__ = report;
          }
        } catch (error) {
          // Error handled silently
        }
      }, 3000); // Wait 3 seconds for service worker to initialize
    };

    performPWACheck();

    // Check if app is already installed
    const checkInstalled = () => {
      const installed =
        window.matchMedia("(display-mode: standalone)").matches ||
        window.navigator.standalone === true ||
        document.referrer.includes("android-app://");
      setIsInstalled(installed);
      return installed;
    };

    // Check installation status immediately
    const currentlyInstalled = checkInstalled();

    // Only skip if app is CURRENTLY installed (running in standalone mode)
    // Don't skip if user just uninstalled and came back to browser
    if (currentlyInstalled) {
      return; // App is currently running in standalone mode
    }

    // Listen for beforeinstallprompt event
    // This event fires when browser thinks the app can be installed
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    // Also check if prompt is already available (for cases where user dismissed it before)
    // Some browsers may not fire the event again, so we check periodically
    const checkForPrompt = () => {
      // Try to trigger the prompt by checking if conditions are met
      // This helps in cases where the event was dismissed
      if (!deferredPrompt && !currentlyInstalled) {
        // Check if we're in a browser that supports installation
        const supportsInstall =
          ("serviceWorker" in navigator &&
            "BeforeInstallPromptEvent" in window) ||
          window.matchMedia("(display-mode: browser)").matches;

        if (supportsInstall) {
          // Force a check - sometimes the prompt becomes available again after page reload
        }
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Check installation status periodically (every 2 seconds)
    const interval = setInterval(() => {
      checkInstalled();
      checkForPrompt();
    }, 2000);

    // Initial check after a short delay (in case event fires late)
    const timeoutId = setTimeout(() => {
      checkForPrompt();
    }, 3000);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt,
      );
      clearInterval(interval);
      clearTimeout(timeoutId);
    };
  }, [deferredPrompt]);

  const handleInstallClick = async () => {
    // Check again if installed (in case status changed)
    const currentlyInstalled =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true ||
      document.referrer.includes("android-app://");

    if (currentlyInstalled) {
      setIsInstalled(true);
      return;
    }

    if (!deferredPrompt) {
      // Show detailed instructions for manual install
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isAndroid = /Android/.test(navigator.userAgent);
      const isChrome =
        /Chrome/.test(navigator.userAgent) &&
        !/Edge|Edg/.test(navigator.userAgent);
      const isEdge = /Edge|Edg/.test(navigator.userAgent);
      const isFirefox = /Firefox/.test(navigator.userAgent);
      const isSafari =
        /Safari/.test(navigator.userAgent) &&
        !/Chrome/.test(navigator.userAgent);
      const isArabic =
        document.documentElement.lang === "ar" ||
        navigator.language.startsWith("ar");

      let message = "";

      if (isIOS) {
        message = isArabic
          ? 'لتثبيت التطبيق على iOS:\n\n1. اضغط على زر المشاركة (Share) في أسفل الشاشة\n2. قم بالتمرير لأسفل واختر "إضافة إلى الشاشة الرئيسية" (Add to Home Screen)\n3. اضغط "إضافة" (Add)'
          : 'To install on iOS:\n\n1. Tap the Share button at the bottom\n2. Scroll down and select "Add to Home Screen"\n3. Tap "Add"';
      } else if (isAndroid) {
        if (isChrome) {
          message = isArabic
            ? 'لتثبيت التطبيق على Android (Chrome):\n\n1. اضغط على القائمة (⋮) في أعلى يمين المتصفح\n2. ابحث عن "تثبيت التطبيق" أو "Install app"\n3. اضغط عليه للتثبيت\n\nأو:\n- ابحث عن أيقونة التثبيت في شريط العنوان\n- أو افتح القائمة وابحث عن "Add to Home screen"'
            : 'To install on Android (Chrome):\n\n1. Tap the menu (⋮) at the top right\n2. Look for "Install app" or "Add to Home screen"\n3. Tap it to install\n\nOr:\n- Look for install icon in address bar\n- Or open menu and find "Add to Home screen"';
        } else if (isFirefox) {
          message = isArabic
            ? 'لتثبيت التطبيق على Android (Firefox):\n\n1. اضغط على القائمة (⋮) في أعلى يمين المتصفح\n2. ابحث عن "الصفحة" أو "Page"\n3. اختر "إضافة إلى الشاشة الرئيسية"'
            : 'To install on Android (Firefox):\n\n1. Tap menu (⋮) at top right\n2. Find "Page" option\n3. Select "Add to Home Screen"';
        } else {
          message = isArabic
            ? 'لتثبيت التطبيق على Android:\n\n1. اضغط على القائمة (⋮) في المتصفح\n2. ابحث عن "تثبيت التطبيق" أو "Install app" أو "Add to Home screen"\n3. اضغط عليه للتثبيت'
            : 'To install on Android:\n\n1. Tap menu (⋮) in browser\n2. Look for "Install app" or "Add to Home screen"\n3. Tap to install';
        }
      } else {
        // Desktop browsers
        if (isChrome) {
          message = isArabic
            ? 'لتثبيت التطبيق على Chrome:\n\n1. ابحث عن أيقونة التثبيت في شريط العنوان (على اليمين)\n2. أو اضغط على القائمة (⋮) → "تثبيت FAYA ERP Platform"\n3. أو اضغط على أيقونة القفل/المعلومات في شريط العنوان → "تثبيت"\n\nملاحظة: قد لا تظهر الأيقونة إذا كان التطبيق مثبتاً مسبقاً أو لا يستوفي الشروط'
            : 'To install on Chrome:\n\n1. Look for install icon in address bar (on the right)\n2. Or click menu (⋮) → "Install FAYA ERP Platform"\n3. Or click lock/info icon in address bar → "Install"\n\nNote: Icon may not appear if app is already installed or doesn\'t meet requirements';
        } else if (isEdge) {
          message = isArabic
            ? 'لتثبيت التطبيق على Edge:\n\n1. ابحث عن أيقونة التثبيت في شريط العنوان\n2. أو اضغط على القائمة (⋯) → "تطبيقات" → "تثبيت هذا الموقع كتطبيق"\n3. أو اضغط على أيقونة القفل في شريط العنوان → "تثبيت"'
            : 'To install on Edge:\n\n1. Look for install icon in address bar\n2. Or click menu (⋯) → "Apps" → "Install this site as an app"\n3. Or click lock icon → "Install"';
        } else if (isFirefox) {
          message = isArabic
            ? 'لتثبيت التطبيق على Firefox:\n\n1. اضغط على القائمة (☰) → "الصفحة" → "إضافة إلى الشاشة الرئيسية"\n2. أو استخدم اختصار Ctrl+Shift+A ثم ابحث عن "Install"'
            : 'To install on Firefox:\n\n1. Click menu (☰) → "Page" → "Add to Home Screen"\n2. Or use Ctrl+Shift+A and search for "Install"';
        } else if (isSafari) {
          message = isArabic
            ? 'لتثبيت التطبيق على Safari:\n\n1. اضغط على "مشاركة" (Share) في شريط الأدوات\n2. اختر "إضافة إلى الشاشة الرئيسية" (Add to Home Screen)\n3. اضغط "إضافة" (Add)'
            : 'To install on Safari:\n\n1. Click "Share" in toolbar\n2. Select "Add to Home Screen"\n3. Tap "Add"';
        } else {
          message = isArabic
            ? 'لتثبيت التطبيق:\n\n1. ابحث في قائمة المتصفح عن خيار "تثبيت التطبيق" أو "Install app"\n2. أو ابحث عن أيقونة التثبيت في شريط العنوان\n3. إذا لم تجدها، تأكد من أن:\n   - الموقع يعمل على HTTPS\n   - Service Worker نشط\n   - Manifest.json موجود وصحيح'
            : 'To install the app:\n\n1. Look in browser menu for "Install app" option\n2. Or look for install icon in address bar\n3. If not found, make sure:\n   - Site is on HTTPS\n   - Service Worker is active\n   - Manifest.json exists and is valid';
        }
      }

      // Show modal instead of alert for better UX
      const userConfirmed = window.confirm(
        message +
          (isArabic
            ? "\n\nهل تريد فتح صفحة المساعدة؟"
            : "\n\nWould you like to open help page?"),
      );

      if (userConfirmed) {
        // Could open a help page or documentation
      }

      return;
    }

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;

    if (outcome === "accepted") {
      setDeferredPrompt(null);
      setShowInstallPrompt(false);
      setIsInstalled(true);
    }
  };

  // Expose PWA check function globally for debugging
  useEffect(() => {
    if (typeof window !== "undefined") {
      window.checkPWA = async () => {
        const report = await runPWACheck();
        return report;
      };
    }
  }, []);

  const value = {
    deferredPrompt,
    showInstallPrompt,
    isInstalled,
    handleInstallClick,
    setShowInstallPrompt,
  };

  return (
    <PWAInstallContext.Provider value={value}>
      {children}
    </PWAInstallContext.Provider>
  );
}

export function usePWAInstall() {
  const ctx = useContext(PWAInstallContext);
  if (!ctx)
    throw new Error("usePWAInstall must be used within PWAInstallProvider");
  return ctx;
}
